import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Landmark, ExternalLink, CheckCircle2 } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 });
// Server-computed: releasable share while pending (shrinks after a refund / duplicate-payment refund),
// the amount actually paid out once released.
const payoutOf = (r) => {
  if (["released", "released_manual"].includes(r.status)) return (Number(r.transferred_cents) || 0) / 100 || Number(r.payout_amount) || 0;
  if (["pending_release", "partially_refunded"].includes(r.status)) return (Number(r.releasable_cents) || 0) / 100;
  return 0;
};

export default function VendorEarnings({ vendors }) {
  // Connect is owner-level: any owned listing id resolves the same account.
  const vendorId = vendors?.[0]?.vendor_id || "";
  const [listingFilter, setListingFilter] = useState("all");
  const [data, setData] = useState(null);
  const [connect, setConnect] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    if (!vendorId) return;
    api.get("/vendors/mine/earnings").then(({ data }) => setData(data)).catch(() => setData(null));
    api.get(`/stripe/connect/status/${vendorId}`).then(({ data }) => setConnect(data)).catch(() => setConnect(null));
  }, [vendorId]);

  useEffect(() => {
    api.get("/stripe/connect/platform-status").then(({ data }) => setPlatform(data)).catch(() => setPlatform({ enabled: false }));
  }, []);

  const startConnect = async () => {
    if (!vendorId) return;
    setConnectLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/stripe/connect/account-link", {
        vendor_id: vendorId,
        return_url: `${origin}/vendor/dashboard?connect=done`,
        refresh_url: `${origin}/vendor/dashboard?connect=retry`,
      });
      window.location.href = data.onboarding_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start Stripe onboarding");
    } finally { setConnectLoading(false); }
  };

  const openDashboard = async () => {
    try {
      const { data } = await api.post(`/stripe/connect/login-link/${vendorId}`);
      window.open(data.login_url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open Stripe dashboard");
    }
  };

  const rows = useMemo(() => (data?.entries || []).filter(r => listingFilter === "all" || r.vendor_id === listingFilter), [data, listingFilter]);
  const totals = useMemo(() => ({
    pending_release: rows.filter(r => ["pending_release", "partially_refunded"].includes(r.status)).reduce((a, r) => a + payoutOf(r), 0),
    released: rows.filter(r => ["released", "released_manual"].includes(r.status)).reduce((a, r) => a + payoutOf(r), 0),
    platform_fees: rows.reduce((a, r) => a + (r.platform_fee || 0), 0),
  }), [rows]);

  if (!vendors?.length) return <div className="text-[color:var(--text-muted)] text-sm" data-testid="earnings-empty">Create a listing first to see earnings.</div>;

  const connected = connect?.connected && connect?.charges_enabled;
  const onboardingInProgress = connect?.connected && !connect?.charges_enabled;

  return (
    <div data-testid="earnings-panel" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={listingFilter} onValueChange={setListingFilter}>
          <SelectTrigger data-testid="earnings-vendor-select" className="bg-white h-11 max-w-xs"><SelectValue placeholder="All listings" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All listings ({vendors.length})</SelectItem>
            {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {data && (
          <div className="ml-auto text-xs text-[color:var(--text-muted)]">
            Platform fee <strong className="text-[color:var(--text)]">{data.platform_fee_percent}%</strong> · You keep <strong className="text-[color:var(--text)]">{data.vendor_payout_percent}%</strong> + 100% of tips
          </div>
        )}
      </div>

      {/* Stripe Connect onboarding banner */}
      <div className="card-elegant p-5 flex flex-wrap items-center gap-4" data-testid="connect-banner">
        <div className="shrink-0 w-11 h-11 rounded-full bg-[color:var(--surface)] flex items-center justify-center">
          {connected
            ? <CheckCircle2 className="w-6 h-6 text-[color:var(--verified)]" />
            : <Landmark className="w-6 h-6 text-[color:var(--accent)]" />}
        </div>
        <div className="flex-1 min-w-[220px]">
          {platform && platform.enabled === false ? (
            <>
              <div className="font-serif text-lg" data-testid="connect-platform-disabled">Direct payouts coming soon</div>
              <div className="text-xs text-[color:var(--text-muted)]">
                NContro's admin needs to finish activating Stripe Connect. In the meantime, your 88% earnings + tips are safely held in escrow (see ledger below).
              </div>
            </>
          ) : connected ? (
            <>
              <div className="font-serif text-lg">Stripe payouts active</div>
              <div className="text-xs text-[color:var(--text-muted)]">One payout account for all {vendors.length > 1 ? `${vendors.length} of your listings` : "your listings"}. We release your payouts after each event is complete.</div>
            </>
          ) : onboardingInProgress ? (
            <>
              <div className="font-serif text-lg">Finish Stripe onboarding</div>
              <div className="text-xs text-[color:var(--text-muted)]">Complete verification to receive direct payouts. Missing: {(connect?.currently_due || []).slice(0, 3).join(", ") || "—"}</div>
            </>
          ) : (
            <>
              <div className="font-serif text-lg">Connect a payout account</div>
              <div className="text-xs text-[color:var(--text-muted)]">Add your bank so your 88% earnings + any tips land directly instead of sitting in escrow.</div>
            </>
          )}
        </div>
        <div className="flex gap-2">
          {platform && platform.enabled === false ? (
            <a href={platform.action_url || "https://dashboard.stripe.com/connect"} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full h-10" data-testid="connect-admin-link">
                <ExternalLink className="w-4 h-4 mr-2" /> Admin: enable Connect
              </Button>
            </a>
          ) : connected ? (
            <Button variant="outline" onClick={openDashboard} data-testid="connect-dashboard-btn" className="rounded-full h-10">
              <ExternalLink className="w-4 h-4 mr-2" /> Stripe dashboard
            </Button>
          ) : (
            <Button onClick={startConnect} disabled={connectLoading} data-testid="connect-start-btn" className="btn-sage rounded-full h-10">
              {connectLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Landmark className="w-4 h-4 mr-2" />}
              {onboardingInProgress ? "Continue onboarding" : "Set up payouts"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-elegant p-6" data-testid="earnings-pending">
          <div className="eyebrow">Pending release</div>
          <div className="font-serif text-3xl mt-2">{fmt(totals.pending_release)}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Held until each event completes</div>
        </div>
        <div className="card-elegant p-6" data-testid="earnings-released">
          <div className="eyebrow">Released to you</div>
          <div className="font-serif text-3xl mt-2 text-[color:var(--verified)]">{fmt(totals.released)}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Paid out via Stripe</div>
        </div>
        <div className="card-elegant p-6" data-testid="earnings-fees">
          <div className="eyebrow">Platform fees</div>
          <div className="font-serif text-3xl mt-2">{fmt(totals.platform_fees)}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Retained by NContro</div>
        </div>
      </div>

      <div>
        <div className="eyebrow mb-3">Ledger</div>
        {rows.length === 0 ? (
          <div className="text-[color:var(--text-muted)] text-sm" data-testid="earnings-no-rows">No payments yet — collected deposits and tips appear here after checkout.</div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-[1.5fr,0.9fr,0.9fr,0.9fr,0.9fr,0.7fr] gap-3 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-4">
              <div>Booking · Listing</div><div>Gross</div><div>Tip</div><div>Platform fee</div><div>Your payout</div><div>Status</div>
            </div>
            {rows.map(r => (
              <div key={r.earning_id} data-testid={`earning-${r.earning_id}`}
                className="grid grid-cols-[1.5fr,0.9fr,0.9fr,0.9fr,0.9fr,0.7fr] gap-3 items-center card-elegant p-4 text-sm">
                <div className="min-w-0">
                  <div className="font-mono text-xs truncate">{r.booking_id}</div>
                  <div className="text-[10px] text-[color:var(--text-muted)] mt-0.5 truncate" data-testid={`earning-listing-${r.earning_id}`}>
                    {r.vendor_name} · {r.event_date?.slice(0,10) || "—"} · {r.processed_via === "connect" ? "Connect" : "Platform"}
                  </div>
                </div>
                <div>{fmt(r.gross_amount)}</div>
                <div>{fmt(r.tip_amount)}</div>
                <div className="text-[color:var(--text-muted)]">− {fmt(r.platform_fee)}</div>
                <div>
                  <div className="font-semibold text-[color:var(--verified)]" data-testid={`earning-payout-${r.earning_id}`}>{fmt(payoutOf(r))}</div>
                  {Math.abs(payoutOf(r) - (Number(r.payout_amount) || 0)) > 0.005 && (
                    <div className="text-[10px] text-[color:var(--text-muted)]" data-testid={`earning-payout-note-${r.earning_id}`}>
                      {r.overpayment_refunded_cents > 0 ? "duplicate payment refunded" : r.amount_refunded > 0 ? "after refund" : `of ${fmt(r.payout_amount)}`}
                    </div>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-widest">
                  <span className={r.status === "released" ? "text-[color:var(--verified)]" : r.status === "refunded" ? "text-[color:var(--accent)]" : "text-[#5C5C5C]"}>
                    {r.status.replace("_", " ")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
